// ============================================================================
// LES JOURS D'UNE LOCATION — ses jours civils, sa progression, et le transit en jours OUVRÉS de part et d'autre.
//
// UTILITÉ : la page Locations dessine ce transit, la correction d'une location annonce le jour où l'appareil revient au
// parc, le tableau de bord dessine où en est chaque location. Une seule règle pour chacun : écrite deux fois, l'une
// compterait un jour de plus que l'autre.
// ============================================================================

/**
 * Le transit reçu avec le parc (`GET /annuaire/devices`), en jours ouvrés — `null` s'il n'est pas lisible.
 * ⚠️ RÉGLÉ DANS LA CONSOLE (décision de l'utilisateur), jamais écrit ici : un « 2 » en dur, recopié sur chaque écran,
 *    survivait au changement de transporteur. Inconnu, il ne se devine pas — l'écran ne dessine pas de transit.
 */
export function transitOf(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null;
}

/** « 2 jours ouvrés », « 1 jour ouvré », « 0 jour ouvré ». */
export function transitPhrase(n: number): string {
  return `${n} jour${n > 1 ? 's' : ''} ouvré${n > 1 ? 's' : ''}`;
}

/** Le jour civil d'une date de location (« 2026-11-01 ») — la Console les enregistre à minuit UTC, seul le jour compte. */
export const rentalDay = (v: string | null): string => (v ? v.slice(0, 10) : '');

/** Le jour civil LOCAL d'un instant (« 2026-10-30 ») : celui de l'écran qui regarde, pas celui de Greenwich. */
export function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Le même jour un an plus tard (« 2026-10-30 » → « 2027-10-30 ») : l'échéance proposée d'une location longue. Un 29 février donne le 1ᵉʳ mars. */
export function oneYearAfter(day: string): string {
  return new Date(Date.UTC(Number(day.slice(0, 4)) + 1, Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)))).toISOString().slice(0, 10);
}

export type RentalRegime = 'courte' | 'longue' | 'achat';

/**
 * Ce qui ne va pas dans un régime et ses dates — `null` quand tout est bon.
 * ⚠️ LA RÈGLE DU SERVEUR (`rentalProblem`), à l'affectation comme à la correction : une location (courte ou longue) a ses
 *    deux dates, la fin après le début ; un achat n'en exige pas. L'écran la montre pour fermer le bouton, le serveur la
 *    refait — sans échéance, une location longue n'a ni alerte ni blocage automatique.
 */
export function rentalDatesProblem(regime: RentalRegime | null, start: string, end: string): string | null {
  if (!regime) return 'Choisissez un régime.';
  if (regime === 'achat') return null;
  if (!start || !end) return 'Renseignez les deux dates.';
  if (end <= start) return regime === 'courte' ? 'Le retour doit suivre le début de la location.' : 'L’échéance doit suivre le début du contrat.';
  return null;
}

/** Numéro d'un jour civil « AAAA-MM-JJ », compté sans heure : un passage à l'heure d'été ne décale rien. */
const dayNumber = (day: string): number => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / 86400000;

/** Où en est une location, vue d'un jour donné — ce que dessine sa barre au tableau de bord. */
export interface RentalProgress {
  /** À venir, en cours, retour dépassé, ou sans date de retour. */
  state: 'upcoming' | 'running' | 'overdue' | 'open';
  /** Durée louée, jour de début et jour de retour compris — `null` sans date de retour. */
  days: number | null;
  /** Part écoulée, de 0 à 100 : la largeur remplie d'une barre qui va du début au retour prévu. */
  elapsedPct: number;
  /** Jours avant le début (à venir) ou de retard (retour dépassé) ; 0 sinon. */
  gap: number;
}

/**
 * La progression d'une location entre son début et son retour prévu (jours « AAAA-MM-JJ »).
 * ⚠️ LA BARRE VA DU DÉBUT AU RETOUR DE CETTE LOCATION, jamais d'une fenêtre commune : ses deux dates sont écrites à ses
 *    deux bouts. Placée dans une fenêtre de 13 jours, une location de deux mois partait au milieu de la ligne et sortait
 *    à droite, sous des dates qui prétendaient couvrir la ligne entière. Comparer les locations entre elles est le rôle du
 *    planning de la page Locations.
 * ⚠️ Le jour même compte comme entamé : le jour du début vaut 1 jour sur N, le jour du retour remplit la barre.
 */
export function rentalProgress(start: string, end: string | null, today: string): RentalProgress {
  const a = dayNumber(start) - dayNumber(today);
  const b = end ? dayNumber(end) - dayNumber(today) : null;
  // Sans retour, ou un retour avant le début (que le serveur refuse) : aucune durée à partager.
  if (b === null || b < a) return { state: 'open', days: null, elapsedPct: 0, gap: Math.max(0, a) };
  const days = b - a + 1;
  if (a > 0) return { state: 'upcoming', days, elapsedPct: 0, gap: a };
  if (b < 0) return { state: 'overdue', days, elapsedPct: 100, gap: -b };
  return { state: 'running', days, elapsedPct: Math.round(((1 - a) / days) * 100), gap: 0 };
}

/** Décale un jour civil de n jours OUVRÉS (le week-end ne compte pas), compté sans heure. */
export function shiftBusinessDays(day: string, n: number): string {
  let d = dayNumber(day);
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    d += step;
    const dow = new Date(d * 86400000).getUTCDay();
    if (dow !== 0 && dow !== 6) left--;
  }
  return new Date(d * 86400000).toISOString().slice(0, 10);
}

/** La barre d'une location et son transit, en parts de la ligne (0 à 100) — avant, la location, après. */
export interface RentalTrack { prePct: number; rentPct: number; postPct: number }

/**
 * Où tombent le transit et la location sur la barre d'une ligne : l'appareil part `transit` jours ouvrés avant le début
 * et revient au parc `transit` jours ouvrés après le retour — les segments que la page Locations hachure, sur la même
 * règle.
 * ⚠️ `null` sans date de retour ou sans transit connu : on ne dessine pas un transit qu'on ne peut pas placer.
 */
export function rentalTrack(start: string, end: string | null, transit: number | null): RentalTrack | null {
  if (!end || transit === null || end < start) return null;
  const pre = dayNumber(start) - dayNumber(shiftBusinessDays(start, -transit));
  const post = dayNumber(shiftBusinessDays(end, transit)) - dayNumber(end);
  const total = pre + (dayNumber(end) - dayNumber(start) + 1) + post;
  const pct = (n: number) => Math.round((n / total) * 1000) / 10;
  const prePct = pct(pre);
  const postPct = pct(post);
  return { prePct, rentPct: Math.round((100 - prePct - postPct) * 10) / 10, postPct };
}

/** Décale une date de n jours OUVRÉS (le week-end ne compte pas). */
export function addBusinessDays(d: Date, n: number): Date {
  const out = new Date(d);
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    out.setDate(out.getDate() + step);
    const dow = out.getDay();
    if (dow !== 0 && dow !== 6) left--;
  }
  return out;
}
